import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/apiClient'
import {
  ProductionRoute,
  ProductionRouteCreatePayload,
  ProductionRouteUpdatePayload,
  ProductionRouteWithReports
} from '@/features/production-routes/api/ProductionRouteModel'
import { PRODUCTION_ROUTES_QUERY_KEY as V2_PRODUCTION_ROUTES_QUERY_KEY } from '@/hooks/api/v2/productionRoutes'

const PRODUCTION_ROUTES_QUERY_KEY = 'productionRoutes'

export const useCreateProductionRoute = (organizationId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: ProductionRouteCreatePayload) => {
      const res = await apiClient.post<ProductionRoute>(`/v1/production-routes`, payload)

      queryClient.invalidateQueries({
        queryKey: [PRODUCTION_ROUTES_QUERY_KEY, organizationId]
      })

      queryClient.invalidateQueries({
        queryKey: [V2_PRODUCTION_ROUTES_QUERY_KEY]
      })

      return res.data
    }
  })
}

export const useGetProductionRoute = (organizationId: string, productionRouteId: string) => {
  return useQuery({
    queryKey: [PRODUCTION_ROUTES_QUERY_KEY, organizationId, productionRouteId],
    queryFn: async () => {
      const res = await apiClient.get<ProductionRouteWithReports>(
        `/v1/production-routes/${productionRouteId}`
      )
      return res.data
    },
    enabled: !!organizationId && !!productionRouteId
  })
}

export const useGetProductionRoutesForInstallation = (
  organizationId: string,
  installationId: string
) => {
  const queryClient = useQueryClient()
  return useQuery({
    queryKey: [PRODUCTION_ROUTES_QUERY_KEY, organizationId, installationId],
    queryFn: async () => {
      const res = await apiClient.get<ProductionRouteWithReports[]>(
        `/v1/organizations/${organizationId}/installations/${installationId}/production-routes`
      )

      for (const productionRoute of res.data) {
        queryClient.setQueryData(
          [PRODUCTION_ROUTES_QUERY_KEY, organizationId, productionRoute.id],
          productionRoute
        )
      }

      return res.data
    },
    enabled: !!organizationId && !!installationId
  })
}

export const useUpdateProductionRoute = (organizationId: string, productionRouteId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: [PRODUCTION_ROUTES_QUERY_KEY, organizationId, productionRouteId],
    mutationFn: async (payload: ProductionRouteUpdatePayload) => {
      const res = await apiClient.patch<ProductionRoute>(
        `/v1/production-routes/${productionRouteId}`,
        payload
      )

      return res.data
    },
    onSuccess: data => {
      queryClient.setQueryData(
        [PRODUCTION_ROUTES_QUERY_KEY, organizationId, productionRouteId],
        data
      )
    }
  })
}

export const useDeleteProductionRoute = (organizationId: string, productionRouteId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: [PRODUCTION_ROUTES_QUERY_KEY, organizationId, productionRouteId],
    mutationFn: async () => {
      await apiClient.post(`/v1/production-routes/${productionRouteId}/delete`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PRODUCTION_ROUTES_QUERY_KEY, organizationId]
      })
    }
  })
}
